import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Sparkles,
  ArrowUpRight,
  Check,
  MessageSquare,
  Zap,
  BarChart3,
  Shield,
  Clock,
  Users,
  Bot,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Target,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: MessageSquare, title: 'Reply suggestions', text: 'Zyra drafts replies based on your knowledge base and conversation history.' },
  { icon: Zap, title: 'Instant summaries', text: 'Get a one-paragraph summary of any long thread in one click.' },
  { icon: BarChart3, title: 'Sentiment detection', text: 'Spot frustrated customers early and escalate before churn happens.' },
  { icon: Target, title: 'Intent routing', text: 'Route conversations to the right team based on what the customer is asking.' },
];

const benefits = [
  { icon: Zap, title: '60% faster replies', description: 'AI drafts help agents respond in seconds instead of minutes.' },
  { icon: Shield, title: 'Brand-safe tone', description: 'Set rules for language, tone, and topics Zyra is allowed to handle.' },
  { icon: Clock, title: 'Always learning', description: 'Train Zyra on past replies so it gets sharper over time.' },
  { icon: Users, title: 'Team productivity', description: 'Let Zyra handle FAQs while agents focus on complex issues.' },
];

const useCases = [
  { title: 'Overflow deflection', text: 'Let Zyra answer common FAQs while agents handle the complex cases.' },
  { title: 'New agent coaching', text: 'First-week agents get real-time suggestions so they sound like veterans.' },
  { title: 'Faster escalation', text: 'Zyra flags urgent issues and pre-fills context for supervisors.' },
  { title: '24/7 availability', text: 'AI responds to common questions even when agents are offline.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'Does Zyra replace agents?',
    answer: 'No. Zyra is an AI co-pilot that drafts, summarizes, and coaches, but humans stay in control of every customer interaction.',
  },
  {
    question: 'Can Zyra be trained on our content?',
    answer: 'Yes. Zyra learns from your help center, saved replies, and conversation history so answers match your brand.',
  },
  {
    question: 'How fast are AI suggestions?',
    answer: 'Zyra generates suggestions in under 500ms median latency, allowing for real-time assistance during conversations.',
  },
  {
    question: 'Is my data used to train shared models?',
    answer: 'No. Customer data is never used to train shared models. Your data stays private and secure.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Zyra AI',
    description: 'Zyra AI is an AI copilot for customer support that drafts replies, summarizes conversations, detects sentiment, and routes intent inside the ZuriHub workspace.',
    url: `${site.baseUrl}/zyra-ai`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'All',
    offers: {
      '@type': 'Offer',
      url: `${site.baseUrl}/zyra-ai`,
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    featureList: [
      'AI reply suggestions',
      'Conversation summaries',
      'Sentiment detection',
      'Intent routing',
      'Brand tone controls',
      'Knowledge base integration',
      'Real-time coaching',
      'Analytics insights',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
  },
];

export default function ZyraAiPage() {
  return (
    <>
      <SEO
        title="Zyra AI - AI Copilot for Customer Support | ZuriHub"
        description="Zyra AI drafts replies, summarizes conversations, detects sentiment, and routes intent inside the ZuriHub workspace. AI copilot that helps agents work faster and smarter."
        path="/zyra-ai"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="AI copilot, customer support AI, AI reply suggestions, conversation summarization, sentiment detection, intent routing, AI for support teams, Zyra AI, AI assistant"
        />
      </SEO>
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Sparkles className="h-4 w-4" /> Pro & Enterprise
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Meet <span className="gradient-text">Zyra</span>, your AI support agent
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                AI copilot that drafts replies, summarizes long threads, detects sentiment, and coaches your team in real time. Built for speed, accuracy, and brand consistency.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Try Zyra <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Sparkles className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/ZYRA.png"
                  alt="Zyra AI showing AI copilot interface with reply suggestions and conversation summaries"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful AI features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything AI can do to help your support team.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose Zyra AI</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform agent productivity.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Zyra reads your help center, canned responses, and past conversations to learn how your team speaks. When a new message arrives, it predicts intent, drafts a reply, and offers it to the agent.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: BookOpen,
                    title: 'Connect knowledge base',
                    description: 'Connect your knowledge base and set tone rules.',
                  },
                  {
                    icon: Bot,
                    title: 'Suggests replies',
                    description: 'Zyra suggests replies as messages come in.',
                  },
                  {
                    icon: Users,
                    title: 'Agent reviews',
                    description: 'Agents edit, approve, or escalate in one click.',
                  },
                  {
                    icon: Clock,
                    title: 'Learns and improves',
                    description: 'Zyra gets sharper with every conversation.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">AI copilot features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Reply suggestions</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Instant summaries</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Sentiment detection</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Intent routing</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Brand tone controls</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Knowledge base creation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Empower your team with AI</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Try Zyra AI and help your agents work faster, sound more consistent, and deliver better customer experiences.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Try Zyra now <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="reliability-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="reliability-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Enterprise reliability</h2>
              <p className="mt-4 text-slate-300">
                Zyra is served from the same global edge as Zuri Cloud. A standard backend engineering team monitors inference, latency, and model updates.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-800/50 p-5">
                  <Users className="h-6 w-6 text-brand-400" />
                  <p className="mt-2 font-bold">Engineering team</p>
                  <p className="text-sm text-slate-400">Dedicated ML and backend engineers on call</p>
                </div>
                <div className="rounded-2xl bg-slate-800/50 p-5">
                  <Clock className="h-6 w-6 text-brand-400" />
                  <p className="mt-2 font-bold">Uptime target</p>
                  <p className="text-sm text-slate-400">99.9% for Zyra AI inference endpoints</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">SLA highlights</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Median suggestion latency under 500ms</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Daily model health checks</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Rollback plan for every model update</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Customer data never used to train shared models</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about Zyra AI</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
